/*
  Event Website registration panel (Eventene 4.11, Phase 5).

  Deliberately small: open, close, and the focus behaviour a dialog owes a
  keyboard or screen-reader user. Everything about WHICH state the panel shows is
  server-rendered into the Turbo frame inside it, so there is no client-side
  state machine here to drift out of step with the server.

  Contract:
    [data-registration-panel]        the dialog element
    [data-registration-panel-open]   any control that opens it
    [data-registration-panel-close]  any control that closes it (also the scrim)
*/

const PANEL = '[data-registration-panel]';
const OPEN = '[data-registration-panel-open]';
const CLOSE = '[data-registration-panel-close]';
const BODY_OPEN_CLASS = 'evw-reg-panel-open';
const EMBED = '[data-registration-embed]';
const CHALLENGE = '[data-registration-challenge]';
// Conflict: the entry session has to be resolved before a grant exists.
const CHALLENGE_STATUS = 409;
const RECAPTCHA_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit';
const REGISTERED_CLASS = 'evw-reg-panel-registered';
const SUMMARY = '[data-registration-summary]';

/*
  How long the embedded handshake may take before the panel gives up (Trello
  #1020).

  Sparrow announces itself with AWAITING_GRANT, we answer with GRANT, and it
  reports READY once it has redeemed it. Every one of those steps used to fail
  SILENTLY: a message lost to a frame swap, a refused grant request, a redemption
  that never happened -- and the participant was left on an animated loader with
  nothing behind it, for as long as they were willing to wait.

  Generous on purpose. This is a backstop for a handshake that has stopped, not a
  performance budget: a slow phone on a slow network must never see it.
*/
const HANDSHAKE_TIMEOUT_MS = 20000;

/*
  The markers bobcat_api2 appends to Stripe's return URL.

  Hosted Checkout redirects window.top unconditionally, so pressing Pay always
  tears the whole tab away from the Event Website and takes the drawer with it
  (Trello #977). The return is aimed back at the event page instead of at
  Sparrow's own origin, and this is what tells us the arrival is a return from
  payment rather than an ordinary visit, so the panel can reopen itself.

  A marker is not a credential and confers nothing: all it does is reopen a
  drawer. Everything about WHO this is comes from the session cookie the return
  navigation carries, and the panel then goes through the ordinary grant flow.
*/
const RETURN_PARAM = 'registration';
const RETURN_VALUES = ['payment_success', 'payment_canceled'];

// Every message Sparrow may send. Anything else is ignored rather than
// dispatched, so adding a message to Sparrow is a deliberate change at both
// ends.
const MESSAGE_SOURCE = 'eventene.registration';
const MESSAGES = {
  AWAITING_GRANT: 'registration.awaiting_grant',
  GRANT: 'registration.grant',
  READY: 'registration.ready',
  // Three facts, deliberately kept apart. SAVED means a persona now has durable
  // registration state, which changes what the hero CTA says. CHANGED means the
  // group or its progress moved -- a person added, an attendance answered -- so
  // the summary beneath the CTA is stale and must be re-read from the server.
  // COMPLETED means the embedded interaction itself has ended, which is the one
  // thing that may take the frame away from the participant.
  SAVED: 'registration.saved',
  CHANGED: 'registration.changed',
  COMPLETED: 'registration.completed',
  CANCELLED: 'registration.cancelled',
  ERROR: 'registration.error',
  /*
    Website -> Sparrow. The panel is part of this page, so the page decides how
    it looks: a dark drawer on a light site reads as a broken frame. Sparrow is
    cross-origin and cannot see this document, which is why the theme is posted
    rather than inherited the way the same-origin Page frames get it in
    page_theme.js.
  */
  THEME: 'registration.theme',
};

// darkmode-js toggles this on <body>, so it is the same source of truth
// page_theme.js reads.
const DARK_BODY_CLASS = 'darkmode--activated';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const RegistrationPanel = {
  _panel: null,
  _lastTrigger: null,
  _recaptchaPromise: null,
  _themeObserver: null,

  start() {
    this._panel = document.querySelector(PANEL);
    // The scroll lock belongs to a panel that is open on THIS page. Turbo can
    // hand us a body carrying the class with no open panel under it -- a cached
    // snapshot taken while the drawer was open, restored by Back -- and the
    // class would then have nothing left able to remove it.
    this._releaseScrollLockUnlessOpen();
    if (!this._panel) return;

    // These four are stable properties, not per-call bound functions, so
    // re-running start() on every turbo:load re-registers identical listeners
    // and the DOM discards the duplicates. Binding here would stack them.
    document.addEventListener('click', this._onClick);
    document.addEventListener('keydown', this._onKeydown);
    // Turbo replaces the frame's contents on every step. Focus would otherwise
    // stay on the button that was just submitted -- which Turbo then removes,
    // dropping focus to <body> and losing a keyboard user entirely.
    document.addEventListener('turbo:frame-load', this._onFrameLoad);
    // Turbo snapshots the page before leaving it. An open drawer would be
    // cached open, so Back restores a locked body around a panel holding a frame
    // that is no longer loaded.
    document.addEventListener('turbo:before-cache', this._onBeforeCache);
    window.addEventListener('message', this._onMessage);
    // Guarded inside, so the repeated start() on each turbo:load observes once.
    this._watchTheme();
    this._reopenAfterPaymentReturn();
  },

  /*
    Coming back from Stripe, reopen the drawer the payment took away.

    The frame is loading="lazy", so on this first load there is no state element
    to inspect and nothing to reload: opening the panel is what causes the frame
    to fetch, and the server then resumes this browser wherever it left off --
    which, for a participant whose session cookie came back with them, is the
    embedded Sparrow state with a freshly minted grant.

    The marker is stripped from the URL afterwards so it does not survive into
    history or a later refresh, where it would reopen the panel on a visit that
    has nothing to do with a payment.
  */
  _reopenAfterPaymentReturn() {
    if (!this._panel || this.isOpen()) return;

    const params = new URLSearchParams(window.location.search);
    if (RETURN_VALUES.indexOf(params.get(RETURN_PARAM)) === -1) return;

    this.open(null);
    this._stripReturnMarker(params);
  },

  _stripReturnMarker(params) {
    if (!window.history || !window.history.replaceState) return;

    params.delete(RETURN_PARAM);
    const query = params.toString();
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname + (query ? `?${query}` : '') + window.location.hash,
    );
  },

  _releaseScrollLockUnlessOpen() {
    if (this.isOpen()) return;

    document.body.classList.remove(BODY_OPEN_CLASS);
  },

  _onBeforeCache: () => {
    RegistrationPanel.close();
    // close() is a no-op for an already-closed panel, and the snapshot must not
    // carry the lock in either case.
    document.body.classList.remove(BODY_OPEN_CLASS);
  },

  /*
    Messages from embedded Sparrow.

    Three checks, and all three matter. The ORIGIN is compared against the origin
    this page actually framed, which the server rendered into the embed element —
    any other frame on the page, and any other site that gets a handle to this
    window, is talking to somebody else. The SOURCE field separates our protocol
    from every other library that posts to window. The TYPE must be one we know.

    Nothing here reads a token or a session out of the payload, because Sparrow
    does not send one: the panel learns that registration finished, not what was
    registered.
  */
  _onMessage: (event) => {
    const embed = document.querySelector(EMBED);
    if (!embed) return;

    const expected = embed.dataset.sparrowOrigin;
    if (!expected || event.origin !== expected) return;

    const data = event.data;
    if (!data || typeof data !== 'object') return;
    if (data.source !== MESSAGE_SOURCE) return;
    if (typeof data.type !== 'string') return;

    RegistrationPanel._handleMessage(data.type);
  },

  _handleMessage(type) {
    switch (type) {
      case MESSAGES.AWAITING_GRANT:
        this._sendGrant();
        // The frame has just told us it is listening, so this is the first
        // moment it can be given the current theme.
        this._sendTheme();
        break;
      case MESSAGES.SAVED:
        // Label only. Sparrow is still mid-interaction — the participant may
        // have other people to register — so the frame is left strictly alone.
        // The summary is refreshed by CHANGED, which Sparrow sends alongside
        // this on the same attendance write.
        this._markRegistered();
        break;
      case MESSAGES.CHANGED:
        // The group or its progress moved. Re-read the summary from the server
        // rather than deriving it from the message, so the panel cannot make
        // the page claim a state nobody saved. Fires while the panel is open,
        // so the count moves as each person is added or answered.
        this._refreshSummary();
        break;
      case MESSAGES.COMPLETED:
        // Only a solo registration sends this: Done for a group returns to
        // Sparrow's own group page instead. For one person there is nothing
        // left to show, so the drawer closes. Reopening from View Registration
        // re-embeds Sparrow fresh (_restartEmbeddedOnReopen), and the summary
        // beneath the hero is re-read from the server on close, so the page
        // claims only what Eventene has actually saved.
        this._markRegistered();
        this.close();
        break;
      case MESSAGES.CANCELLED:
        this.close();
        break;
      case MESSAGES.READY:
        // The handshake completed: Sparrow has redeemed the grant and is showing
        // registration. Nothing left to watch for.
        this._clearHandshakeWatchdog();
        this._markEmbedReady();
        break;
      case MESSAGES.ERROR:
        // Sparrow reached a state it can explain to the participant itself, so
        // the watchdog must not replace that with our own message.
        this._clearHandshakeWatchdog();
        this._markEmbedReady();
        break;
      default:
        break;
    }
  },

  isOpen() {
    return this._panel && !this._panel.hidden;
  },

  open(trigger) {
    if (!this._panel || this.isOpen()) return;

    this._restartEmbeddedOnReopen();
    this._lastTrigger = trigger || null;
    this._panel.hidden = false;
    // Only locked while the panel is open, and only on the body: a mobile
    // Event Website behind an open drawer should not scroll under it, but it
    // must scroll perfectly well again the moment the drawer closes.
    document.body.classList.add(BODY_OPEN_CLASS);
    this._focusFirst();
  },

  close() {
    if (!this.isOpen()) return;

    this._panel.hidden = true;
    this._clearHandshakeWatchdog();
    document.body.classList.remove(BODY_OPEN_CLASS);
    if (this._lastTrigger && document.contains(this._lastTrigger)) {
      this._lastTrigger.focus();
    }
    this._lastTrigger = null;
    // Nobody touched the panel -> nothing to re-read. CHANGED already refreshed
    // any panel that did something, so this only covers the plain open/close.
    if (document.body.classList.contains(REGISTERED_CLASS)) this._refreshSummary();
  },

  /*
    Re-ask the server for the summary rather than deriving it from what Sparrow
    said: the postMessage payloads are hints, and a summary built from them
    would state as fact something no one verified.

    A failed fetch leaves the stale summary standing, which is wrong but quiet
    -- the next page load corrects it.
  */
  _refreshSummary() {
    const summary = document.querySelector(SUMMARY);
    const url = summary && summary.dataset.registrationSummaryUrl;
    if (!url) return;

    fetch(url, { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then((response) => (response.ok ? response.text() : null))
      .then((html) => {
        if (html === null) return;
        summary.innerHTML = html;
        this._syncCtaLabel();
      })
      .catch(() => {});
  },

  // Keep the CTA's head count in step with the group. The server renders it
  // right on page load; this is what moves "View Registration (2)" to "(3)"
  // when a person is added mid-session, using the label the fragment carries so
  // the count and the wording stay the server's, never guessed here.
  _syncCtaLabel() {
    const body = document.querySelector(`${SUMMARY} [data-registration-view-label]`);
    const label = body && body.dataset.registrationViewLabel;
    if (!label) return;

    const registered = document.body.classList.contains(REGISTERED_CLASS);
    document.querySelectorAll(`${OPEN}[data-registered-label]`).forEach((control) => {
      const el = control.querySelector('.button__text') || control;
      // Update the visible text when the button is already in its registered
      // state -- either this session marked it (REGISTERED_CLASS) or the server
      // rendered it that way, which shows as the text still matching the old
      // registered label. A button still reading "Register" is left alone.
      const showingRegistered = registered
        || el.textContent.trim() === (control.getAttribute('data-registered-label') || '').trim();
      control.setAttribute('data-registered-label', label);
      if (showingRegistered) el.textContent = label;
    });
  },

  // Where focus goes when the drawer opens and after each frame swap.
  //
  // The field first, the Close button only when the state has none: the first
  // focusable in the panel is Close, and landing there after every step put a
  // keyboard user on the X instead of on the code or the name they were asked
  // for.
  //
  // Left alone when focus is already inside the frame. Turbo renders the new
  // content a frame before it dispatches turbo:frame-load, so a fast visitor
  // (or a browser driver) can already be typing when this runs; pulling focus
  // then cut the address off mid-word and sent the rest of the keystrokes to
  // the Close button.
  _focusFirst() {
    const frame = document.getElementById('event_registration_panel');
    const active = document.activeElement;
    if (frame && active && frame.contains(active)) return;

    const target = (frame && frame.querySelector(FOCUSABLE)) || this._panel.querySelector(FOCUSABLE);
    if (target) target.focus();
  },

  _onClick: (event) => {
    const opener = event.target.closest(OPEN);
    if (opener) {
      event.preventDefault();
      RegistrationPanel.open(opener);
      return;
    }
    if (event.target.closest('[data-registration-retry]')) {
      event.preventDefault();
      RegistrationPanel._retryHandshake();
      return;
    }
    if (event.target.closest(CLOSE)) {
      event.preventDefault();
      RegistrationPanel.close();
    }
  },

  _onKeydown: (event) => {
    if (!RegistrationPanel.isOpen()) return;

    if (event.key === 'Escape') {
      RegistrationPanel.close();
      return;
    }
    if (event.key === 'Tab') RegistrationPanel._trapTab(event);
  },

  // Keeps Tab inside the dialog while it is open, so focus cannot wander into
  // the Event Website behind it and leave the user typing into a page they
  // cannot see.
  _trapTab(event) {
    const focusable = Array.from(this._panel.querySelectorAll(FOCUSABLE))
      .filter((el) => el.offsetParent !== null);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  },

  /*
    Opening the drawer is a fresh entry, not a resume.

    Sparrow is a single-page app inside an iframe that nobody unmounts: closing
    the drawer only hides it. So whatever route the participant last navigated
    to inside Sparrow was still on screen when they pressed View Registration,
    because nothing had reloaded the frame.

    Reloading re-embeds Sparrow at its registration entry (/personas: the
    persona list, with attendance state and the way through to answers), which
    is what View Registration is asking for.

    ONLY THE STATE THAT EMBEDS SPARROW. The Bobcat-owned steps are forms -- an
    email being typed, a code half-entered, a CAPTCHA solved but not submitted
    -- and reloading one of those would throw away what the participant had put
    in it and, for a challenge, ask them to do it again.
  */
  _reentryStates: ['ready'],

  _restartEmbeddedOnReopen() {
    const current = document.querySelector('#event_registration_panel [data-registration-state]');
    if (!current) return;
    if (this._reentryStates.indexOf(current.dataset.registrationState) === -1) return;

    this._reloadFrame();
  },

  // Ask the server what state the panel should be in now.
  //
  // Reassigning src to the same value does NOT refetch — Turbo ignores it — so
  // the frame is reloaded explicitly. `complete` is the attribute Turbo sets
  // once a frame has loaded; clearing it and re-setting src is the fallback for
  // versions without reload().
  _reloadFrame() {
    const frame = document.getElementById('event_registration_panel');
    if (!frame) return;

    // Not the frame's current src: after a form submission that is the POST
    // URL, which cannot be re-fetched with GET. The canonical state path comes
    // from the server on the PANEL, not on the embed.
    const stateUrl = this._panel && this._panel.dataset.stateUrl;
    if (!stateUrl) return;

    frame.removeAttribute('complete');
    frame.setAttribute('src', stateUrl);
  },

  // The Event Website behind the panel is stale the moment registration state
  // is saved. The panel does not know the page's markup, so it marks the
  // document and swaps the label on any control that declared one. Idempotent:
  // several personas may each announce a save during one interaction.
  _markRegistered() {
    document.body.classList.add(REGISTERED_CLASS);
    document.querySelectorAll(`${OPEN}[data-registered-label]`).forEach((control) => {
      const label = control.querySelector('.button__text') || control;
      label.textContent = control.dataset.registeredLabel;
    });
    document.dispatchEvent(new CustomEvent('eventene:registration-completed'));
  },

  /*
    The credential never touches the page: it is fetched over the session's own
    connection and posted straight to Sparrow's origin, so it appears in no
    markup, no URL and no history entry.
  */
  async _sendGrant() {
    const embed = document.querySelector(EMBED);
    const frame = embed && embed.querySelector('iframe');
    if (!embed || !frame) return;

    try {
      const token = document.querySelector('meta[name="csrf-token"]');
      const response = await fetch(embed.dataset.grantUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'X-CSRF-Token': (token && token.content) || '',
          Accept: 'application/json',
        },
      });
      // The server needs a person before it will mint a grant. It answers the
      // challenge markup with 409 (Trello #3769) -- not a failure, a step. The
      // panel shows the server's own challenge rather than its load error, and
      // the ordinary form inside it resumes the flow: it posts to identify,
      // targets this frame, and the next state carries the embed again.
      if (response.status === CHALLENGE_STATUS) {
        const markup = await response.text();
        this._showChallenge(markup);
        return;
      }

      if (!response.ok) {
        // Every non-OK answer lands here -- refused, rate limited, or a fault.
        // The participant is told the same thing either way, and told it HERE:
        // the old code returned silently on the belief that "the frame shows its
        // own state when no grant arrives", which is not true. Sparrow only
        // explains itself when something REACHES it; when nothing does it waits.
        this._failHandshake();
        return;
      }

      const { grant } = await response.json();
      if (!grant) {
        this._failHandshake();
        return;
      }

      frame.contentWindow.postMessage(
        { source: MESSAGE_SOURCE, type: MESSAGES.GRANT, payload: { grant } },
        embed.dataset.sparrowOrigin,
      );
    } catch (e) {
      this._failHandshake();
    }
  },

  /*
    Start watching for a handshake that never completes.

    Armed when the frame that carries Sparrow appears, cleared by READY or ERROR,
    and it fires the same recoverable state a refused grant produces. This is what
    covers the failure nobody can otherwise detect: an AWAITING_GRANT lost to a
    Turbo frame swap, which Sparrow announces exactly once and never repeats.
  */
  _armHandshakeWatchdog() {
    this._clearHandshakeWatchdog();
    const embed = document.querySelector(EMBED);
    if (!embed) return;

    // The server names the deadline so it can be shortened under test, where
    // waiting twenty seconds for a backstop is not practical. Anything
    // unparseable falls back to the constant rather than to no deadline at all.
    const declared = parseInt(embed.dataset.handshakeTimeout, 10);
    const timeout = Number.isFinite(declared) && declared > 0
      ? declared
      : HANDSHAKE_TIMEOUT_MS;

    this._handshakeTimer = window.setTimeout(() => this._failHandshake(), timeout);
  },

  _clearHandshakeWatchdog() {
    if (!this._handshakeTimer) return;

    window.clearTimeout(this._handshakeTimer);
    this._handshakeTimer = null;
  },

  /*
    Replace the frame with something the participant can act on.

    Deliberately a LOADING failure, not "registration is unavailable": the
    registration is fine, we could not start it. The wording, and every other
    string here, is rendered by the server onto the embed element -- the panel
    speaks no English of its own, and nothing about grants, rate limits or status
    codes reaches the screen.

    Retry re-runs the ordinary path: the frame is reloaded, a new Sparrow
    announces itself and the server mints a grant exactly as it would on a first
    open. RegistrationGrant.issue! retires any outstanding grant for the
    participant in the same transaction, so retrying cannot leave two usable
    credentials behind, and it grants nothing the first attempt would not have.
  */
  _failHandshake() {
    this._clearHandshakeWatchdog();
    const embed = document.querySelector(EMBED);
    if (!embed || embed.dataset.handshakeFailed === '1') return;

    embed.dataset.handshakeFailed = '1';
    const d = embed.dataset;
    embed.innerHTML = '';
    const wrap = document.createElement('div');
    wrap.className = 'evw-reg__unavailable';
    wrap.innerHTML = [
      `<h2 class="evw-reg__title">${d.failedTitle || ''}</h2>`,
      `<p class="evw-reg__lede">${d.failedLede || ''}</p>`,
      '<div class="evw-reg__actions">',
      `<button type="button" class="button button--square evw-reg__submit"`,
      ` data-registration-retry="true">${d.failedRetry || ''}</button>`,
      `<button type="button"`,
      ` class="button button--square button--white button--stroke button--light evw-reg__secondary"`,
      ` data-registration-panel-close="true">${d.failedClose || ''}</button>`,
      '</div>',
    ].join('');
    embed.appendChild(wrap);
  },

  // Start over from the panel's own state, which re-embeds Sparrow and mints a
  // fresh grant through the ordinary route. Nothing here is a shortcut past the
  // server's checks.
  // Sparrow has something on screen, so the spinner the ready state draws over
  // the frame can go. Attribute rather than removal: the frame may be reloaded
  // for a retry, and the server renders the spinner fresh with the state.
  _markEmbedReady() {
    const embed = document.querySelector(EMBED);
    if (embed) embed.dataset.embedReady = '1';
  },

  /*
    Put the server's challenge into the frame and let it be answered.

    The markup is the SAME partial the ordinary entry steps render, so the widget
    boots through the same path (_renderChallenge) and the form inside it is the
    ordinary one -- it posts to identify, carries the address and a fresh flow
    token, and declares this frame as its target, so Turbo drives the response
    back into the drawer exactly as it does for every other step. Nothing about
    reCAPTCHA is reimplemented here.

    The watchdog is stopped because the handshake is now waiting on a person
    rather than on a message, and twenty seconds of a human reading a checkbox is
    not a failure. It is armed again by turbo:frame-load when the answered
    challenge brings the embed back.
  */
  _showChallenge(markup) {
    const frame = document.getElementById('event_registration_panel');
    if (!frame) return;

    this._clearHandshakeWatchdog();
    frame.innerHTML = markup;
    this._renderChallenge();
    this._focusFirst();
  },

  _retryHandshake() {
    const embed = document.querySelector(EMBED);
    if (embed) delete embed.dataset.handshakeFailed;
    this._reloadFrame();
  },

  /*
    Posts the page's current light/dark state to the embedded frame.

    Sent to the exact origin the server rendered into the embed element -- the
    same value inbound messages are checked against -- never '*', so the theme
    goes to Sparrow and to nothing else that may be framed on this page.
  */
  _sendTheme() {
    const embed = document.querySelector(EMBED);
    const frame = embed && embed.querySelector('iframe');
    if (!embed || !frame || !frame.contentWindow) return;

    frame.contentWindow.postMessage(
      {
        source: MESSAGE_SOURCE,
        type: MESSAGES.THEME,
        payload: { theme: document.body.classList.contains(DARK_BODY_CLASS) ? 'dark' : 'light' }
      },
      embed.dataset.sparrowOrigin
    );
  },

  /*
    Observed rather than polled, and observed on <body> rather than wired to the
    toggle, so the frame follows the theme however it was flipped -- the
    darkmode-js control, a restored preference, or anything else that sets the
    class. Same approach page_theme.js takes for the same reason.
  */
  _watchTheme() {
    if (this._themeObserver) return;

    this._themeObserver = new window.MutationObserver(() => this._sendTheme());
    this._themeObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
  },

  _onFrameLoad: (event) => {
    if (event.target.id !== 'event_registration_panel') return;
    if (!RegistrationPanel.isOpen()) return;

    RegistrationPanel._renderChallenge();
    RegistrationPanel._focusFirst();
    // The frame has just been replaced. If this state carries the embed, the
    // handshake starts now and gets a deadline (Trello #1020).
    RegistrationPanel._armHandshakeWatchdog();
    // Each panel step is a frame navigation: email, the code, login, the
    // name/surname form. The one that authorises the session is what makes the
    // summary have anything to show, and there is no message for it -- it is
    // same-origin, inside our own frame -- so re-read after every step. The
    // server returns nothing until a session exists, so the early steps are
    // a cheap no-op.
    RegistrationPanel._refreshSummary();
  },

  /*
    The bot challenge, rendered by hand.

    Turbo swaps a frame's contents by assigning innerHTML, and scripts inserted
    that way are inert -- so Rails' recaptcha_tags, which relies on a <script>
    arriving with the markup, would leave an unanswerable empty box on the page.
    The widget is therefore rendered explicitly: api.js is loaded once and
    lazily -- never on an ordinary visit, which is the whole point of a
    progressive challenge -- and grecaptcha.render is called against the
    container the server sent.
  */
  _renderChallenge() {
    const el = document.querySelector(CHALLENGE);
    if (!el || el.dataset.rendered === '1') return;

    el.dataset.rendered = '1';
    this._loadRecaptcha()
      .then(() => window.grecaptcha.render(el, { sitekey: el.dataset.siteKey }))
      .catch(() => {
        // The script could not load: an outage, or a network that blocks
        // Google. The server refuses the request either way, so this decides
        // only what the participant reads while that is true.
        el.dataset.rendered = '';
        el.textContent = el.dataset.unavailableText || '';
      });
  },

  _loadRecaptcha() {
    if (this._recaptchaPromise) return this._recaptchaPromise;

    this._recaptchaPromise = new Promise((resolve, reject) => {
      if (window.grecaptcha && window.grecaptcha.render) { resolve(); return; }
      const script = document.createElement('script');
      script.src = RECAPTCHA_SRC;
      script.async = true;
      script.defer = true;
      script.onerror = reject;
      // api.js finishes initialising asynchronously after load; grecaptcha.ready
      // is the documented signal that render() is safe to call.
      script.onload = () => window.grecaptcha.ready(resolve);
      document.head.appendChild(script);
    });
    return this._recaptchaPromise;
  },
};

export default RegistrationPanel;
